import {AfterViewInit, Component, ElementRef, Inject, OnInit, Renderer2, ViewChild, ViewEncapsulation} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {ProductionScheduleDrawingViewMode} from '@domain/production-schedule/production-schedule-drawing-view-mode.enum';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {Permission} from '@domain/profile/permission.enum';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {HttpStatusBarService} from '@infrastructure/http/status-bar/http-status-bar.service';
import {OrderLinesCustomSettingsDialogComponent} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-settings.component';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {DrawingLibraryHelper} from '@presentation/pages/texedit/drawing-library/drawing-library-new-listview/drawing-library-helper';
import {
  AssertionUtils,
  ColDefBuilderFactoryService,
  DialogBuilderFactoryService,
  GridModel,
  GridOptionsBuilderFactoryService,
  LoadingCellOverlayComponent,
  NoDataOverlayComponentParams,
  StatusBar,
  TranslateService,
  UuidUtils
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {CellMouseOverEvent, ColumnVisibleEvent, GetRowIdParams, GridOptions, GridReadyEvent, IRowNode, RowHeightParams, RowNode, RowSelectedEvent} from 'ag-grid-community';
import {takeUntil} from 'rxjs';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../../plan/production-schedule-plan';
import {GroupedDesignBaseComponent} from '../grouped-design-base/grouped-design-base.component';
import {DrawingsGroupedDetailCellRendererComponent} from './detail-cell-renderer/drawings-grouped-detail-cell-renderer.component';
import {GroupedDrawing} from './grouped-drawing';
import {GroupedDrawingsSelectionService} from './grouped-drawings-selection.service';

@Component({
  selector: 'app-drawings-grouped-view',
  templateUrl: './drawings-grouped-view.component.html',
  styleUrls: ['./drawings-grouped-view.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DrawingsGroupedViewComponent extends GroupedDesignBaseComponent implements OnInit, AfterViewInit {
  @ViewChild('container')
  public containerElement: ElementRef;

  @ViewChild('groupedGrid')
  public groupedGrid: AgGridAngular;

  public loadingForTheFirstTime = true;
  public groupedGridOptions: GridOptions;
  public groupedGridModel: GridModel;
  public detailGridModel: GridModel;
  public groupedDrawings: GroupedDrawing[];
  public statusBar: StatusBar;

  public readonly customSettingsComponent = OrderLinesCustomSettingsDialogComponent;

  public readonly MAX_DETAIL_ROW_HEIGHT = 350;
  private readonly DETAIL_ROW_HEIGHT = 96;

  public constructor(
    @Inject(AUTHENTICATION) authentication: Authentication,
    @Inject(DRAWINGS) drawings: Drawings,
    @Inject(PRODUCTION_SCHEDULE_PLAN) productionSchedulePlan: ProductionSchedulePlan,
    gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    colDefBuilderFactory: ColDefBuilderFactoryService,
    statusBarService: HttpStatusBarService,
    translate: TranslateService,
    groupedDrawingsSelectionService: GroupedDrawingsSelectionService,
    dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly draggingService: DraggingService,
    private readonly renderer: Renderer2,
    drawingLibraryHelper: DrawingLibraryHelper
  ) {
    super(
      authentication,
      drawings,
      productionSchedulePlan,
      gridOptionsBuilderFactoryService,
      colDefBuilderFactory,
      statusBarService,
      translate,
      dialogBuilderFactoryService,
      groupedDrawingsSelectionService,
      drawingLibraryHelper
    );
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.initialiseGroupedGridOptions();

    const currentSubscription = this.authentication.getCurrentSubscription();

    this.hasQuantitySuggestionPermission = currentSubscription.hasPermission(Permission.TEXFAB_QUANTITY_SUGGESTION);

    this.groupedDrawingsSelectionService.getGroupedDrawingsSelected().subscribe((selectedDrawings: ListDrawingConfiguration[]) => {
      this.preselectedDrawings = selectedDrawings;
    });
    this.groupedDrawingsSelectionService.getdetailRowSelected().subscribe((detailComponent: {parentNode: IRowNode; parentSelectionState: boolean; drawingsSelected: ListDrawingConfiguration[]}) => {
      this.onDetailRowsSelectionChanged(detailComponent.parentNode as RowNode, detailComponent.parentSelectionState, detailComponent.drawingsSelected);
    });

    this.groupedDrawingsSelectionService.selectedPath.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((selectedPath: ProductionSchedulePath) => {
      this.selectedPathOutput.emit(selectedPath);
    });

    this.groupedDrawingsSelectionService.getdetailRowSelected().subscribe((detailComponent: {parentNode: IRowNode; parentSelectionState: boolean; drawingsSelected: ListDrawingConfiguration[]}) => {
      this.onDetailRowsSelectionChanged(detailComponent.parentNode as RowNode, detailComponent.parentSelectionState, detailComponent.drawingsSelected);
    });

    this.groupedDrawingsSelectionService.selectedRestPathColoredYarnSet.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((coloredYarnSet: OverviewListColoredYarnSetWithStartDent) => {
      this.selectedRestPathColoredYarnSetOutput.emit(coloredYarnSet);
    });

    if (!this.isPreSelection) {
      this.viewModes.push(ProductionScheduleDrawingViewMode.GROUPED_DESIGN_THUMBNAILS);
      this.selectedViewModeChanged.emit(this.selectedViewMode);
    }
  }

  public ngAfterViewInit(): void {
    this.setGroupedDesignBaseComponentProperties(this.groupedGrid);
  }

  private initialiseGroupedGridOptions(): void {
    const drawingsGroupedDetailCellRendererComponent = new DrawingsGroupedDetailCellRendererComponent(
      this.drawings,
      this.productionSchedulePlan,
      this.gridOptionsBuilderFactoryService,
      this.colDefBuilderFactory,
      this.groupedDrawingsSelectionService,
      this.translate,
      this.dialogBuilderFactoryService,
      this.draggingService,
      this.renderer,
      this.drawingLibraryHelper
    );

    const defaultGroupedGridOptions = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getGroupColumnDefinitions(), GridIdentifier.GROUPED_DESIGNS)
      .withHeaderHeight(32)
      .withFloatingFiltersHeight(32)
      .withServerSideDataSource(this.SLICE_SIZE, this.getGroupedServerSideDataSource())
      .withMasterDetail(DrawingsGroupedDetailCellRendererComponent, true, (params: any) => this.getDetailCellRendererParams(params), false)
      .withGetRowHeight((params: RowHeightParams) => this.getRowHeightForDetailRow(params))
      .withComponents({
        customLoadingCellRenderer: LoadingCellOverlayComponent,
        DrawingsGroupedDetailCellRendererComponent: drawingsGroupedDetailCellRendererComponent
      })
      .withLoadingCellRenderer('customLoadingCellRenderer')
      .withNoRowsOverlay({
        scale: 0.7,
        titleParam: 'DESIGN_LIBRARY.DESIGN',
        hideDescription: true,
        isAnyFilterPresent: () => !AssertionUtils.isNullOrUndefined(this.groupedGrid) && Object.values(this.groupedGrid?.api?.getFilterModel())?.length > 0
      } as NoDataOverlayComponentParams)
      .withOnGridReady((event: GridReadyEvent) => {
        event.api.refreshCells();
        event.api.refreshHeader();
        this.setInitializationFilters();
      })
      .withOnCellMouseOver((event: CellMouseOverEvent) => this.onCellMouseOver(event))
      .withRowSelection(this.isPreSelection, false, false, !this.isPreSelection)
      .withGetRowId((params: GetRowIdParams) => UuidUtils.generateV4Uuid())
      .withCompactView()
      .withOnColumnVisible((event: ColumnVisibleEvent) => this.onColumnVisibleChange(event))
      .withLoadingOverlay();

    if (this.isPreSelection) {
      this.groupedGridOptions = defaultGroupedGridOptions.withOnRowSelected((event: RowSelectedEvent) => this.parentNodeSelected(event)).build();
    } else {
      this.groupedGridOptions = defaultGroupedGridOptions.build();
    }
  }

  private onColumnVisibleChange(event: any): void {
    this.groupedGrid.api.refreshServerSide();
  }

  private getRowHeightForDetailRow(params: RowHeightParams): number {
    const isDetailRow = params.node.detail;

    if (!isDetailRow) {
      return undefined;
    }

    return Math.min(
      this.MAX_DETAIL_ROW_HEIGHT,
      this.DETAIL_ROW_HEADER_HEIGHT + 2 * this.DETAIL_ROW_VERTICAL_PADDING + this.DETAIL_ROW_SCROLL_BAR + params.data.drawingIds.length * this.DETAIL_ROW_HEIGHT
    );
  }
}
